// StepsView.tsx — routes of the redesigned funnel (design/try). Screen order: docs in MavroKentavro/Lola_Funnel
// (docs/migration.md §3). /email and /choosePlan keep their production URLs and production logic; the paywall is the
// production component with the new look (choosePlane.tsx).
//
// The production Header / Button (driven by GlobalState.currentStep) are not rendered: every new screen draws its own
// header, progress bar and buttons. GlobalState itself stays mounted (App.tsx) — it still saves email / language and
// sends web-onboarding-email when the paywall opens; ProductionSync feeds it from the funnel's answers.
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from '../shared/scrollToTop';
import Loader from '../shared/loader';
import { FunnelStateProvider } from '../../funnel/adapters/FunnelState';
import { ProductionSync } from '../../funnel/adapters/productionSync';
import { TrackBridge } from '../../funnel/adapters/analytics';
import { useScreenView } from '../../funnel/useScreenView';
import '../../styles/funnel/index.scss';

// Lazy-loaded step components
const Start = lazy(() => import('./start'));
const Language = lazy(() => import('./language'));
const Tutor = lazy(() => import('./tutor'));
const QuizIntro = lazy(() => import('./quizIntro'));
const Level = lazy(() => import('./level'));
const Words = lazy(() => import('./words'));
const VocabResult = lazy(() => import('./vocabResult'));
const GrammarTest = lazy(() => import('./grammarTest'));
const GrammarResult = lazy(() => import('./grammarResult'));
const CompoundQuiz = lazy(() => import('./compoundQuiz'));
const Sentiment = lazy(() => import('./sentiment'));
const ImageWord = lazy(() => import('./imageWord'));
const WordSenseResult = lazy(() => import('./wordSenseResult'));
const PlanReady = lazy(() => import('./planReady'));
const Name = lazy(() => import('./name'));
const StudyHistory = lazy(() => import('./studyHistory'));
const Goal = lazy(() => import('./goal'));
const DailyTime = lazy(() => import('./dailyTime'));
const Struggles = lazy(() => import('./struggles'));
const Topics = lazy(() => import('./topics'));
const PlanSummary = lazy(() => import('./planSummary'));
const Personalizing = lazy(() => import('./personalizing'));
const EmailPlan = lazy(() => import('./emailPlan'));
const EmailPreview = lazy(() => import('./emailPreview'));
const Unsubscribe = lazy(() => import('./unsubscribe'));

// Heavy payment components - loaded separately
const ChoosePlaneWithPayment = lazy(() =>
    import('../../providers/paymentProvider/PaymentProviderFactory').then(({ PaymentProviderFactory }) =>
        import('./choosePlane').then(({ default: ChoosePlane }) => ({
            default: () => (
                <PaymentProviderFactory>
                    <ChoosePlane />
                </PaymentProviderFactory>
            )
        }))
    )
);

function FunnelRoutes() {
    useScreenView();

    return (
        <Routes>
            <Route path="/" element={<Navigate to="/start" replace />} />
            <Route path="/start" element={<Start />} />
            <Route path="/language" element={<Language />} />
            <Route path="/tutor" element={<Tutor />} />
            <Route path="/quizIntro" element={<QuizIntro />} />
            <Route path="/level" element={<Level />} />
            <Route path="/words" element={<Navigate to="/words/0" replace />} />
            <Route path="/words/:index" element={<Words />} />
            <Route path="/vocabResult" element={<VocabResult />} />
            <Route path="/grammar" element={<Navigate to="/grammarTest/0" replace />} />
            <Route path="/grammarTest/:index" element={<GrammarTest />} />
            <Route path="/grammarResult" element={<GrammarResult />} />
            <Route path="/compoundQuiz" element={<Navigate to="/compoundQuiz/0" replace />} />
            <Route path="/compoundQuiz/:index" element={<CompoundQuiz />} />
            <Route path="/imageWord/:index" element={<ImageWord />} />
            <Route path="/sentiment" element={<Navigate to="/sentiment/0" replace />} />
            <Route path="/sentiment/:index" element={<Sentiment />} />
            <Route path="/wordSenseResult" element={<WordSenseResult />} />
            <Route path="/planReady" element={<PlanReady />} />
            <Route path="/name" element={<Name />} />
            <Route path="/studyHistory" element={<StudyHistory />} />
            <Route path="/goal" element={<Goal />} />
            <Route path="/dailyTime" element={<DailyTime />} />
            <Route path="/struggles" element={<Struggles />} />
            <Route path="/topics" element={<Topics />} />
            <Route path="/planSummary" element={<PlanSummary />} />
            <Route path="/personalizing" element={<Personalizing />} />
            <Route path="/email" element={<EmailPlan />} />
            <Route path="/emailPreview" element={<EmailPreview />} />
            <Route path="/choosePlan" element={<ChoosePlaneWithPayment />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="*" element={<Navigate to="/start" replace />} />
        </Routes>
    );
}

function StepsView() {
    return (
        <FunnelStateProvider>
            <TrackBridge />
            <ProductionSync />
            <ScrollToTop />
            <div className="app lola-funnel">
                <Suspense fallback={<Loader />}>
                    <FunnelRoutes />
                </Suspense>
            </div>
        </FunnelStateProvider>
    );
}

export default StepsView;
